import { useState, useRef, useEffect, useCallback } from 'react';
import {
    pollHistoryForSidebar,
    cancelHistoryPolling,
    checkDeerFlowReportsStatus,
    cleanupDeerFlowLocalStorage,
    deerFlowManager,
    startDeerFlowGlobalPolling,
    clearDeerFlowGlobalPolling,
    handleSidebarOpen
} from './useDeerFlow';
import { handleDeerFlowHistory } from '@/api/grt/ai';
import {
    migrateStoppedStates,
    createReportManager,
    getSidebarStyle,
    createReportContentUpdateHandler,
    createSidebarOpenHandler,
    createSidebarCloseHandler,
    createReportCompleteFromHistoryHandler,
    createReportEventHandlers,
    registerReportEventListeners,
    removeReportEventListeners,
    createGlobalPollingManager,
    createCloseSidebarEventHandler
} from './useReportUtils';

/**
 * DeerFlow聊天相关功能的自定义Hook
 * 封装了侧边栏、报告管理、全局轮询等所有DeerFlow相关功能
 */
const useDeerFlowChat = ({
    sessionId,
    originMsgMapRef,
    stopDeerFlow,
    stopHistoryPolling,
    detailPanelOpen,
    setDetailPanelOpen,
    activeDetailMessageId,
    setActiveDetailMessageId,
    windowSize,
    isDeerFlowSession = false // 新增：是否为 DeerFlow 会话的标识
}) => {
    // 侧边栏状态管理
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [activeReportId, setActiveReportId] = useState(null);
    // 添加用户手动关闭状态标记
    const [userManualClosed, setUserManualClosed] = useState(false);
    // 🎯 添加强制重新渲染的状态，用于触发标题更新
    const [titleRefreshTrigger, setTitleRefreshTrigger] = useState(0);

    // 报告数据状态
    const [reportData, setReportData] = useState({
        activityData: '',
        reportContent: '',
        threadId: '',
        reportId: null,
        title: '研究报告'
    });
    // 使用useRef存储报告数据，避免不必要的状态更新与重渲染
    const reportDataRef = useRef({
        threadId: '',
        reportId: null,
        activityData: '',
        reportContent: '',
        title: '研究报告',
        isFinal: false,
        timestamp: 0
    });
    // 控制卡片状态的引用对象
    const reportNoticeStateUpdaters = useRef({});
    // 存储每个报告的生成状态
    const [generatingReports, setGeneratingReports] = useState({});
    // 在状态定义部分添加activeReportCardId
    const [activeReportCardId, setActiveReportCardId] = useState(null);
    // 添加正在生成的报告ID状态
    const [activeGeneratingReportId, setActiveGeneratingReportId] = useState(null);
    // 在组件添加强制重渲染机制
    const [sidebarRenderKey, setSidebarRenderKey] = useState(0);

    // 🔥 新增：创建报告管理器实例
    const reportManager = useRef(createReportManager()).current;
    // 添加一个状态来触发全局轮询检查
    const [globalPollingTrigger, setGlobalPollingTrigger] = useState(0);

    // 🔥 新增：存储历史记录轮询数据的状态
    const [historyPollingData, setHistoryPollingData] = useState(null);

    // 创建一个ref来存储触发函数，这样可以在事件处理器中访问
    const triggerGlobalPollingCheckRef = useRef(null);

    // 🔥 新增：创建全局轮询管理器实例
    const globalPollingManager = useRef(
        createGlobalPollingManager({
            startDeerFlowGlobalPolling,
            clearDeerFlowGlobalPolling,
            deerFlowManager,
            handleDeerFlowHistory,
            onHistoryDataUpdate: setHistoryPollingData // 🔥 新增：历史数据更新回调
        })
    ).current;

    // 用于缓存已创建的报告组件，避免重复创建
    const reportComponentsCache = useRef({});

    // 初始化报告状态检查函数
    const checkReportsStatus = async () => {
        await checkDeerFlowReportsStatus(originMsgMapRef, sessionId, setGeneratingReports, reportDataRef, triggerGlobalPollingCheckRef.current);
    };

    // 添加一个函数来手动触发全局轮询检查
    const triggerGlobalPollingCheck = useCallback(() => {
        setGlobalPollingTrigger((prev) => prev + 1);
    }, []);

    // 将函数存储到ref中
    triggerGlobalPollingCheckRef.current = triggerGlobalPollingCheck;

    // 🎯 监听会话切换事件，强制重新渲染以更新标题
    useEffect(() => {
        function handleSessionSwitched() {
            setTitleRefreshTrigger((prev) => prev + 1);
        }

        window.addEventListener('session-switched', handleSessionSwitched);
        return () => window.removeEventListener('session-switched', handleSessionSwitched);
    }, []);

    // 🔥 监听 sessionId 变化，切换会话时关闭多模态学习详情面板
    // 使用 ref 记录上一次的 sessionId，只在真正切换会话时关闭抽屉
    const prevSessionIdRef = useRef(null); // 🔧 修复：初始值设为 null，而不是 sessionId
    useEffect(() => {
        const prevSessionId = prevSessionIdRef.current;
        const currentSessionId = sessionId;

        // 只在真正切换会话时关闭抽屉（从一个有效会话切换到另一个有效会话）
        // 不在首次创建会话时关闭（从 null/undefined 到有值）
        if (prevSessionId && currentSessionId && prevSessionId !== currentSessionId) {
            setDetailPanelOpen(false);
            setActiveDetailMessageId(null);
        }

        // 更新 ref
        prevSessionIdRef.current = currentSessionId;
    }, [sessionId, setDetailPanelOpen, setActiveDetailMessageId]);

    // 🔥 新增：监听消息列表变化，当新消息包含多模态学习结果时，如果抽屉是打开的，更新 activeDetailMessageId
    useEffect(() => {
        if (!sessionId || !detailPanelOpen) {
            return;
        }

        const messages = originMsgMapRef?.current?.[sessionId]?.messages || [];
        if (messages.length === 0) {
            return;
        }

        // 获取最后一条消息（最新的消息）
        const lastMessage = messages[messages.length - 1];

        // 检查最后一条消息是否包含多模态学习结果
        const hasMultimodalResult =
            lastMessage?.multimodal_learning_result &&
            Array.isArray(lastMessage.multimodal_learning_result?.data) &&
            lastMessage.multimodal_learning_result.data.length > 0;

        if (hasMultimodalResult) {
            const lastMessageId = lastMessage._id || messages.length - 1;

            // 如果抽屉是打开的，但 activeDetailMessageId 不匹配最后一条消息
            // 说明新消息返回了，应该更新 activeDetailMessageId 为新消息的 ID
            if (activeDetailMessageId !== lastMessageId) {
                setActiveDetailMessageId(lastMessageId);
            }
        }
    }, [originMsgMapRef, sessionId, detailPanelOpen, activeDetailMessageId, setActiveDetailMessageId]);

    // 在组件挂载时清理本地存储（仅 DeerFlow 会话）
    useEffect(() => {
        if (!isDeerFlowSession) return;

        cleanupDeerFlowLocalStorage();

        // 🔥 新增：清理可能残留的选中报告ID
        try {
            localStorage.removeItem('selected_report_id');
        } catch (error) {
            console.warn('清理残留选中报告ID失败:', error);
        }
    }, [isDeerFlowSession]);

    // 在会话数据加载完成后检查报告状态（仅 DeerFlow 会话）
    useEffect(() => {
        if (!isDeerFlowSession) return;

        // 延迟执行，确保 originMsgMapRef 已经加载
        const timer = setTimeout(() => {
            if (originMsgMapRef?.current?.[sessionId]?.messages?.length > 0) {
                checkReportsStatus();
            }
        }, 1000);

        return () => clearTimeout(timer);
    }, [sessionId, isDeerFlowSession]); // 当sessionId变化时重新检查

    // 监听历史数据加载完成后立即检查报告状态（仅 DeerFlow 会话）
    useEffect(() => {
        if (!isDeerFlowSession) return;

        const checkTimer = setTimeout(() => {
            checkReportsStatus();
        }, 500);

        return () => clearTimeout(checkTimer);
    }, [originMsgMapRef, isDeerFlowSession]); // 监听originMsgMapRef的变化

    // 处理研究报告内容更新的事件监听器（仅 DeerFlow 会话）
    useEffect(() => {
        if (!isDeerFlowSession) return;

        // 创建报告内容更新处理器
        const handleReportContentUpdate = createReportContentUpdateHandler({
            activeReportCardId,
            reportDataRef,
            sidebarOpen,
            userManualClosed,
            activeReportId,
            reportNoticeStateUpdaters,
            setGeneratingReports,
            setSidebarOpen,
            setActiveReportId,
            setActiveReportCardId,
            setUserManualClosed,
            setSidebarRenderKey,
            setReportData,
            triggerGlobalPollingCheckRef,
            setActiveGeneratingReportId
        });

        window.addEventListener('report-content-update', handleReportContentUpdate);

        return () => {
            window.removeEventListener('report-content-update', handleReportContentUpdate);
        };
    }, [
        activeReportId,
        activeReportCardId,
        userManualClosed,
        sidebarOpen,
        reportNoticeStateUpdaters,
        setGeneratingReports,
        setSidebarOpen,
        setActiveReportId,
        setActiveReportCardId,
        setUserManualClosed,
        setSidebarRenderKey,
        setReportData,
        setActiveGeneratingReportId,
        isDeerFlowSession
    ]); // 🔥 修复：添加所有必要的依赖项

    // 处理侧边栏的打开和关闭
    // 修改handleSidebarOpen函数，确保打开侧边栏时能正确加载数据
    const baseSidebarOpenHandler = createSidebarOpenHandler({
        handleSidebarOpenFromFlowData: handleSidebarOpen,
        sessionId,
        reportDataRef,
        triggerGlobalPollingCheckRef,
        setSidebarOpen,
        setActiveReportId,
        setActiveReportCardId,
        setUserManualClosed,
        setSidebarRenderKey,
        setReportData
    });

    // 包装侧边栏打开处理函数，添加关闭多模态详情面板的逻辑
    const handleSidebarOpenLocal = (...args) => {
        // 🔥 打开侧边栏时关闭多模态学习详情面板
        setDetailPanelOpen(false);
        setActiveDetailMessageId(null);
        // 调用原始的处理函数
        return baseSidebarOpenHandler(...args);
    };

    const handleSidebarClose = createSidebarCloseHandler({
        setSidebarOpen,
        setUserManualClosed,
        setActiveReportCardId,
        reportNoticeStateUpdaters,
        reportDataRef
    });

    // 监听关闭侧边栏事件（仅 DeerFlow 会话）
    useEffect(() => {
        if (!isDeerFlowSession) return;

        const handleCloseSidebarEvent = createCloseSidebarEventHandler(handleSidebarClose);

        window.addEventListener('close-report-sidebar', handleCloseSidebarEvent);

        return () => {
            window.removeEventListener('close-report-sidebar', handleCloseSidebarEvent);
        };
    }, [handleSidebarClose, isDeerFlowSession]);

    // 监听从历史记录发现已完成报告的事件（仅 DeerFlow 会话）
    useEffect(() => {
        if (!isDeerFlowSession) return;

        const handleReportCompleteFromHistory = createReportCompleteFromHistoryHandler({
            generatingReports,
            setGeneratingReports,
            reportDataRef,
            triggerGlobalPollingCheckRef
        });

        // 使用reportUtils中的事件处理器
        const reportEventHandlers = createReportEventHandlers({
            setReportData,
            reportDataRef,
            triggerGlobalPollingCheckRef,
            activeReportId,
            setActiveReportId,
            activeReportCardId,
            setActiveReportCardId,
            sidebarOpen,
            setGeneratingReports,
            deerFlowManager,
            setSidebarRenderKey
        });

        // 注册事件监听器
        registerReportEventListeners(reportEventHandlers);

        return () => {
            removeReportEventListeners(reportEventHandlers);
        };
    }, [isDeerFlowSession]);

    // 全局轮询管理 - 独立于侧边栏显示状态（仅 DeerFlow 会话）
    useEffect(() => {
        if (!isDeerFlowSession) return;

        globalPollingManager(reportDataRef, sidebarOpen, activeReportCardId, globalPollingTrigger);
    }, [globalPollingTrigger, activeReportCardId, sidebarOpen, isDeerFlowSession]); // 添加依赖

    // 组件卸载时清理全局轮询（仅 DeerFlow 会话）
    useEffect(() => {
        if (!isDeerFlowSession) return;

        return () => {
            // 组件卸载时清理全局轮询
            clearDeerFlowGlobalPolling();
        };
    }, [isDeerFlowSession]);

    // 创建切换会话时的包装函数
    const handleChangeSession = (changeSessionFunc) => (session) => {
        if (sidebarOpen) {
            handleSidebarClose(reportData.reportId);
        }
        // 切换会话时清除当前活跃的报告卡片ID，避免不同会话间的冲突
        setActiveReportCardId(null);
        // 🎯 强制触发标题重新计算
        setTitleRefreshTrigger((prev) => prev + 1);
        // 将提取的参数传递给changeSession
        changeSessionFunc(session);
    };

    // 创建切换技能时的包装函数
    const handleChangeSkill = (changeSkillFunc) => (...args) => {
        if (sidebarOpen) {
            handleSidebarClose(reportData.reportId);
        }
        // 切换技能时清除当前活跃的报告卡片ID
        setActiveReportCardId(null);
        changeSkillFunc(...args);
    };

    // 创建开始新会话时的包装函数
    const handleStartNewSession = (startNewMsgFunc) => (...args) => {
        if (sidebarOpen) {
            handleSidebarClose(reportData.reportId);
        }
        // 🔥 开始新会话时关闭多模态学习详情面板
        if (detailPanelOpen) {
            setDetailPanelOpen(false);
            setActiveDetailMessageId(null);
        }
        // 开始新会话时清除当前活跃的报告卡片ID
        setActiveReportCardId(null);
        startNewMsgFunc(...args);
    };

    // 创建切换工具时的包装函数
    const handleChangeTool = (changeToolFunc) => async (tool, isLogin) => {
        if (isLogin) {
            // 切换工具时关闭侧边栏
            if (sidebarOpen) {
                handleSidebarClose(reportData.reportId);
            }
            changeToolFunc();
        }
    };

    // 处理报告通知卡片的状态逻辑
    const getReportNoticeStatus = (msg, possibleIds) => {
        // 🔥 最高优先级：检查历史记录数据中的task_status_map，如果为-1则表示失败
        if (historyPollingData?.data?.task_status_map) {
            const taskStatusMap = historyPollingData.data.task_status_map;
            // 检查所有可能的ID在task_status_map中的状态
            for (const id of possibleIds) {
                if (taskStatusMap[id] === -1) {
                    return 'error'; // 返回错误状态，这是最高优先级
                }
            }
        }

        // 🔥 修复：优先使用消息中的状态，特别是对于已停止的任务
        if (msg.question?.status) {
            // 如果消息中的状态是error或stopped，直接返回，不再等待历史数据
            if (msg.question.status === 'error' || msg.question.status === 'stopped') {
                return msg.question.status;
            }
            // 对于其他状态，如果历史数据还在加载中，也优先使用消息状态
            if (!historyPollingData && msg.question.status !== 'loading') {
                return msg.question.status;
            }
            return msg.question.status;
        }

        // 如果正在生成，状态为loading
        const isGenerating = possibleIds.some((id) => generatingReports[id] === true);
        if (isGenerating) {
            return 'loading';
        }

        // 🎯 修复：检查本地存储中是否有完成的报告
        const hasCompletedReport = possibleIds.some((id) => {
            try {
                const unifiedData = JSON.parse(localStorage.getItem('deerflow_reports_data') || '{"completed":{},"generating":{}}');
                const completedReports = unifiedData.completed || {};
                const isCompleted = completedReports[id] && completedReports[id].isFinal === true;
                return isCompleted;
            } catch (e) {
                return false;
            }
        });

        if (hasCompletedReport) {
            return 'success';
        }

        // 如果所有ID的生成状态都未定义，则为loading（初始状态）
        const allUndefined = possibleIds.every((id) => generatingReports[id] === undefined);
        if (allUndefined) {
            return 'loading';
        }
        // 否则为success
        return 'success';
    };

    // 获取报告标题的逻辑
    const getReportTitle = (msg, possibleIds, isGenerating) => {
        // 如果是正在生成的报告，始终显示"研究报告"
        if (isGenerating) {
            return '研究报告';
        }
        // 如果消息状态是loading，也显示"研究报告"
        if (msg.question?.status === 'loading') {
            return '研究报告';
        }

        // 🎯 优先从本地存储获取已完成报告的真实标题
        // titleRefreshTrigger 确保会话切换时重新计算 (trigger: " + titleRefreshTrigger + ")
        for (const id of possibleIds) {
            try {
                const unifiedData = JSON.parse(localStorage.getItem('deerflow_reports_data') || '{"completed":{},"generating":{}}');
                const completedReports = unifiedData.completed || {};
                if (completedReports[id] && completedReports[id].isFinal === true && completedReports[id].title && completedReports[id].title !== '研究报告') {
                    return completedReports[id].title;
                }
            } catch (e) {
                // 忽略错误，继续检查其他来源
            }
        }

        // 如果有reportDataRef中的标题且ID匹配，使用真实标题
        if (
            reportDataRef.current &&
            reportDataRef.current.title &&
            possibleIds.some(id => id === reportDataRef.current.uniqueReportId)
        ) {
            return reportDataRef.current.title;
        }
        // 如果消息中有真实标题（不是默认标题），使用它
        if (msg.question?.title && msg.question.title !== '研究报告') {
            return msg.question.title;
        }
        // 否则显示默认标题
        return '研究报告';
    };

    // 获取当前活跃的报告ID
    const getActiveReportId = () => {
        // 优先使用侧边栏选中的报告ID，如果侧边栏未打开或未选中报告，则使用生成中的报告ID
        return sidebarOpen && activeReportId
            ? activeReportId
            : Object.keys(generatingReports).find((id) => generatingReports[id] === true);
    };

    // 获取停止按钮的回调函数
    const getStopCallback = () => {
        const currentActiveReportId = Object.keys(generatingReports).find(
            (id) => generatingReports[id] === true
        );
        return () => stopDeerFlow(currentActiveReportId);
    };

    return {
        // 状态
        sidebarOpen,
        setSidebarOpen,
        activeReportId,
        setActiveReportId,
        userManualClosed,
        setUserManualClosed,
        titleRefreshTrigger,
        setTitleRefreshTrigger,
        reportData,
        setReportData,
        reportDataRef,
        reportNoticeStateUpdaters,
        generatingReports,
        setGeneratingReports,
        activeReportCardId,
        setActiveReportCardId,
        activeGeneratingReportId,
        setActiveGeneratingReportId,
        sidebarRenderKey,
        setSidebarRenderKey,
        globalPollingTrigger,
        setGlobalPollingTrigger,
        historyPollingData,
        setHistoryPollingData,
        reportManager,
        globalPollingManager,
        reportComponentsCache,

        // 函数
        handleSidebarOpenLocal,
        handleSidebarClose,
        triggerGlobalPollingCheck,
        handleChangeSession,
        handleChangeSkill,
        handleStartNewSession,
        handleChangeTool,
        getReportNoticeStatus,
        getReportTitle,
        getActiveReportId,
        getStopCallback,
        checkReportsStatus,

        // 样式相关
        getSidebarStyle: () => getSidebarStyle(windowSize, sidebarOpen)
    };
};

export default useDeerFlowChat;
